import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Printer, ArrowUpRight } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import ZoneDiagram from "@/components/site/ZoneDiagram";
import Artifacts from "@/components/site/Artifacts";
import CsatStory from "@/components/site/CsatStory";
import CaseStory from "@/components/site/CaseStory";
import { CASE_STUDIES } from "@/data/portfolio";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics";
import { T, useLang } from "@/lib/i18n";


export default function CaseStudy() {
  const { t } = useLang();
  const { slug } = useParams();
  const cs = CASE_STUDIES.find((c) => c.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (cs) {
      document.title = `${cs.title}, Sinthu Nathan`;
      track("case_study_open", { slug: cs.slug });
    }
  }, [cs]);

  if (!cs) return <Navigate to="/" replace />;

  const idx = CASE_STUDIES.findIndex((c) => c.slug === slug);
  const next = CASE_STUDIES[(idx + 1) % CASE_STUDIES.length];

  return (
    <>
      <Navigation />
      <main data-testid={`case-study-page-${slug}`} className="bg-[#faf8f5]">
        {/* Hero */}
        <section className="py-10 lg:py-14 border-b border-[#e5e0d8]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <Link
                to="/#projects"
                className="inline-flex items-center gap-2 text-sm text-[#55625b] hover:text-[#9a7218]"
                data-testid="case-study-back"
              >
                <ArrowLeft className="h-4 w-4" /> {t("All projects")}
              </Link>
              <Link
                to={`/case-studies/${cs.slug}/print`}
                target="_blank"
                rel="noreferrer"
                data-testid="case-study-print-link"
                onClick={() => track("print_view", { slug: cs.slug })}
                className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-[#14352a] text-xs font-medium text-[#14352a] hover:bg-[#14352a] hover:text-white transition-colors"
              >
                <Printer className="h-3.5 w-3.5" /> {t("One-page PDF for interview notes")}
              </Link>
            </div>
            <div className="mt-6 font-mono-label text-[11px] text-[#9a7218]">
              {cs.label}
            </div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#14352a] max-w-4xl">
              {t(cs.title)}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#55625b] max-w-3xl leading-relaxed">
              {t(cs.subtitle)}
            </p>

            <div className="mt-8 aspect-[16/7] overflow-hidden bg-[#f2ece4] border border-[#e5e0d8]">
              <img
                src={cs.hero}
                alt={cs.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        <CaseStory story={cs.story} org={cs.story?.org} />

        {/* Results band */}
        <section data-testid="case-results" className="py-10 lg:py-14 border-t border-[#e5e0d8] bg-[#14352a] text-[#faf8f5]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div className="max-w-2xl">
                <div className="font-mono-label text-[11px] text-[#d9a92e]">{t("Results")}</div>
                <h2 className="mt-3 font-display text-3xl sm:text-4xl leading-tight">{t("What changed.")}</h2>
              </div>
            </div>
            <dl data-testid="case-results-metrics" className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
              {cs.metrics.map((m, i) => (
                <div key={i} data-testid={`case-metric-${i}`} className="border-t-2 border-[#d9a92e] pt-4">
                  <dt className="font-display text-4xl sm:text-5xl leading-none">{m.value}</dt>
                  <dd className="mt-2 text-sm text-[#faf8f5]/70 leading-snug">{t(m.label)}</dd>
                </div>
              ))}
            </dl>
            <ul data-testid="case-results-outcomes" className="mt-10 grid md:grid-cols-2 gap-x-10 gap-y-4 max-w-5xl">
              {cs.outcomes.map((o, i) => (
                <li key={i} className="flex gap-4 border-t border-white/10 pt-4">
                  <span className="font-display text-xl text-[#d9a92e] leading-none pt-0.5">↳</span>
                  <span className="text-base leading-relaxed">{t(o)}</span>
                </li>
              ))}
            </ul>
            {cs.story?.quote && (
              <figure data-testid="case-results-quote" className="mt-10 pt-8 border-t border-white/10 max-w-4xl">
                <blockquote className="font-display italic text-xl sm:text-2xl leading-snug text-[#faf8f5]">
                  &ldquo;{cs.story.quote.text}&rdquo;
                </blockquote>
                <figcaption className="mt-3 font-mono-label text-[9px] text-[#d9a92e]">{cs.story.quote.who} · {t("verbatim")}</figcaption>
              </figure>
            )}
          </div>
        </section>

        {/* Signature visual, food industry only */}
        {slug === "food-industry-ur" && (
          <section className="py-10 lg:py-14 border-t border-[#e5e0d8] bg-[#f2ece4]">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
              <div className="max-w-2xl">
                <div className="font-mono-label text-[11px] text-[#9a7218]">
                  {t("Signature artifact")}
                </div>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl leading-tight text-[#14352a]">
                  {t("Hygienic zones, the physical grammar of the food factory.")}
                </h2>
                <p className="mt-4 text-[#55625b] leading-relaxed">
                  {t("Every zone rewrites the requirements for an industrial digital product. Tap through them.")}
                </p>
              </div>
              <div className="mt-10">
                <ZoneDiagram />
              </div>
            </div>
          </section>
        )}

        {/* Research artefacts */}
        <Artifacts items={cs.artifacts} note={cs.artifactsNote} />

        {/* CSAT insight → action (Sip & Serve) */}
        <CsatStory csat={cs.csat} />

        {/* Gallery, sip and serve */}
        {slug === "sip-and-serve" && cs.gallery && (
          <section className="py-10 lg:py-14 border-t border-[#e5e0d8]">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
              <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                  <div className="font-mono-label text-[11px] text-[#9a7218]">
                    {t("Product gallery")}
                  </div>
                  <h2 className="mt-4 font-display text-3xl sm:text-4xl leading-tight text-[#14352a]">
                    {t("The Sip & Serve TCO admin dashboard, live analytics, surveys, comments.")}
                  </h2>
                </div>
                {cs.liveUrl && (
                  <a href={cs.liveUrl} target="_blank" rel="noreferrer" onClick={() => track("external_link", { label: "sip-serve-live-app" })}>
                    <Button
                      data-testid="case-study-live-link"
                      variant="outline"
                      className="rounded-full border-[#14352a] text-[#14352a] hover:bg-[#14352a] hover:text-white"
                    >
                      {t("Visit live app")}
                      <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Button>
                  </a>
                )}
              </div>
              <div className="mt-8 grid grid-cols-3 gap-4 max-w-3xl">
                {cs.gallery.map((src, i) => (
                  <a
                    key={i}
                    href={src}
                    target="_blank"
                    rel="noreferrer"
                    className="aspect-[9/16] overflow-hidden bg-[#f2ece4] border border-[#e5e0d8] hover:border-[#14352a] transition-colors"
                    data-testid={`sipserve-shot-${i}`}
                  >
                    <img
                      src={src}
                      alt={`Sip & Serve admin dashboard screenshot ${i + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Reflection */}
        <section className="py-10 lg:py-14 border-t border-[#e5e0d8]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="font-mono-label text-[11px] text-[#9a7218]">
                {t("Reflection")}
              </div>
              <h2 className="mt-4 font-display text-3xl leading-tight text-[#14352a]">
                {t("What I’d do differently.")}
              </h2>
            </div>
            <div className="lg:col-span-8">
              <p className="font-display italic text-2xl leading-snug text-[#14352a] border-l-2 border-[#9a7218] pl-6">
                {t(cs.reflection)}
              </p>
            </div>
          </div>
        </section>

        {/* Next */}
        <section className="py-10 lg:py-14 border-t border-[#e5e0d8] bg-[#f2ece4]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="font-mono-label text-[11px] text-[#86928a]">
                {t("Next case study")}
              </div>
              <Link
                to={`/case-studies/${next.slug}`}
                className="mt-3 block font-display text-3xl sm:text-4xl text-[#14352a] hover:text-[#9a7218] transition-colors max-w-2xl"
                data-testid="case-study-next"
              >
                {t(next.title)} →
              </Link>
            </div>
            <Link to="/#contact">
              <Button className="btn-accent rounded-full h-11 px-6">
                {t("Talk to Sinthu")}
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
